import { useMemo, useState } from 'react';
import { ArrowLeftRight, ArrowRight, GitMerge, Search, Undo2, X } from 'lucide-react';
import { suggestAliases } from '../logic/muscles';
import { renameExerciseEverywhere } from '../logic/names';
import { FONT_BODY, FONT_MONO } from '../theme';
import { Input } from '../ui';
import { extraColors } from './common';
import { applyUndo, exerciseNameRows, mergePreview, undoPatch } from './namesData';
import { shortDate } from './statsData';

function fmtTop(top) {
  if (!top) return '—';
  return top.weight > 0 ? `${top.weight} × ${top.reps}` : `${top.reps} reps`;
}

function range(first, last) {
  if (!first) return '—';
  return first === last ? shortDate(first) : `${shortDate(first)} – ${shortDate(last)}`;
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function SectionTitle({ t, children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase', margin: '16px 0 8px' }}>
      {children}
    </div>
  );
}

/* One name of the pair, as a column of the side-by-side preview. */
function Side({ t, side, title, strong }) {
  return (
    <div style={{
      minWidth: 0, borderRadius: 10, padding: '10px 10px 8px',
      background: strong ? t.accentSoft : t.surfaceAlt, border: `1px solid ${strong ? t.accentBorder : t.cardBorder}`,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: strong ? t.accent : t.textFaint }}>{title}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: t.text, marginTop: 4, lineHeight: 1.3, overflowWrap: 'anywhere' }}>{side.name}</div>
      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>{plural(side.sessions, 'session')}</div>
      <div style={{ fontSize: 11, color: t.textMuted }}>{range(side.firstDate, side.lastDate)}</div>
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {side.tops.length === 0 && <span style={{ fontSize: 11, color: t.textFaint }}>No completed sets</span>}
        {side.tops.map(s => (
          <span key={s.date} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: 12 }}>
            <span style={{ fontFamily: FONT_MONO, fontWeight: 600, color: t.text, whiteSpace: 'nowrap' }}>{fmtTop(s)}</span>
            <span style={{ color: t.textFaint, whiteSpace: 'nowrap' }}>{shortDate(s.date)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* What a merge of `from` into `to` does, and the buttons to do it. */
function MergePreview({ t, c, state, from, to, onMerge, onFlip }) {
  const p = useMemo(() => mergePreview(state, from, to), [state, from, to]);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
        <Side t={t} side={p.from} title="Folds away" />
        <Side t={t} side={p.to} title="Keeps" strong />
      </div>
      <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5, marginTop: 8 }}>
        {plural(p.dates.length, 'date')} move to the kept name.
        {p.planDays.length > 0 && ` The plan (${p.planDays.join(', ')}) switches to it too.`}
        {' '}Typing the old name later finds the kept one.
      </div>
      {p.overlapping.length > 0 && (
        <div style={{ fontSize: 12, color: c.amber, lineHeight: 1.5, marginTop: 4 }}>
          Both names were logged on {p.overlapping.map(shortDate).join(', ')}: those sets get joined into one list.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={() => onMerge(from, to, p.dates.length)} style={{
          flex: 1, minHeight: 44, borderRadius: 10, border: `1px solid ${t.accent}`, background: t.accent, color: '#fff',
          fontSize: 13, fontWeight: 700, fontFamily: FONT_BODY, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}><GitMerge size={15} /> Merge</button>
        {onFlip && (
          <button type="button" onClick={onFlip} title="Keep the other name" style={{
            minHeight: 44, padding: '0 12px', borderRadius: 10, border: `1px solid ${t.cardBorder}`, background: 'transparent',
            color: t.textMuted, fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}><ArrowLeftRight size={14} /> Keep other</button>
        )}
      </div>
    </div>
  );
}

function Suggestion({ t, c, state, s, open, onToggle, onMerge }) {
  const [flipped, setFlipped] = useState(false);
  const [from, to] = flipped ? [s.to, s.from] : [s.from, s.to];
  const reason = s.reason === 'normalised' ? 'Same name, other spelling' : `${Math.round(s.similarity * 100)}% alike`;
  return (
    <div style={{ border: `1px solid ${open ? t.accentBorder : t.cardBorder}`, borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={{
        width: '100%', minHeight: 40, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontFamily: FONT_BODY, display: 'block',
      }}>
        <span style={{ display: 'block', fontSize: 13, color: t.textMuted, lineHeight: 1.35 }}>{from}</span>
        <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.35, marginTop: 2 }}>
          <ArrowRight size={14} color={t.accent} style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ minWidth: 0 }}>{to}</span>
        </span>
        <span style={{ display: 'block', fontSize: 11, color: t.textFaint, marginTop: 4 }}>
          {reason} · never on the same day · similar loads · {open ? 'hide' : 'tap to preview'}
        </span>
      </button>
      {open && (
        <MergePreview t={t} c={c} state={state} from={from} to={to} onMerge={onMerge} onFlip={() => setFlipped(f => !f)} />
      )}
    </div>
  );
}

function NameRow({ t, c, state, row, names, open, onToggle, onMerge }) {
  const [target, setTarget] = useState('');
  const others = names.filter(n => n !== row.name);
  return (
    <div style={{ borderBottom: `1px solid ${t.divider}`, opacity: row.stale && !open ? 0.6 : 1 }}>
      <button type="button" onClick={onToggle} aria-expanded={open} style={{
        width: '100%', minHeight: 48, padding: '8px 0', background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontFamily: FONT_BODY, display: 'block',
      }}>
        <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' }}>{row.name}</span>
          <span style={{ fontSize: 12, fontFamily: FONT_MONO, fontWeight: 600, color: t.text, flexShrink: 0 }}>{fmtTop(row.top)}</span>
        </span>
        <span style={{ display: 'block', fontSize: 11, color: t.textMuted, marginTop: 2, lineHeight: 1.4 }}>
          {plural(row.sessions, 'session')} · {range(row.firstDate, row.lastDate)}
          {row.top && ` · top set ${shortDate(row.top.date)}`}
        </span>
        {(row.planDays.length > 0 || row.alias) && (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {row.planDays.map((d, i) => (
              <span key={`${d}-${i}`} style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, background: t.surfaceAlt, border: `1px solid ${t.cardBorder}`, borderRadius: 999, padding: '1px 7px' }}>{d}</span>
            ))}
            {row.alias && (
              <span style={{ fontSize: 10, fontWeight: 600, color: c.amber, background: c.amberSoft, borderRadius: 999, padding: '1px 7px' }}>
                merged into {row.alias}
              </span>
            )}
          </span>
        )}
      </button>
      {open && (
        <div style={{ paddingBottom: 12 }}>
          <select value={target} aria-label={`Merge ${row.name} into`} onChange={e => setTarget(e.target.value)} style={{
            width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: 8,
            background: t.inputBg, color: t.text, border: `1px solid ${t.inputBorder}`,
            fontSize: 13, fontFamily: FONT_BODY, outline: 'none',
          }}>
            <option value="">Merge into…</option>
            {row.alias && others.includes(row.alias) && <option value={row.alias}>{row.alias} (its new name)</option>}
            {others.filter(n => n !== row.alias).map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {target && (
            <MergePreview t={t} c={c} state={state} from={row.name} to={target} onMerge={onMerge} />
          )}
        </div>
      )}
    </div>
  );
}

/* Every logged exercise name, the pairs that look like one lift under two
 * names (logic/muscles suggestAliases) and a merge through logic/names
 * renameExerciseEverywhere, so a rename stops splitting history, PRs and
 * charts. Merges can be undone until the sheet closes; the undo is kept in
 * memory only and restores just the paths the merge rewrote. */
export function ExerciseNamesSheet({ t, isDark, state, setState, today, onMerged, onClose }) {
  const c = extraColors(isDark);
  const [openKey, setOpenKey] = useState(null);
  const [query, setQuery] = useState('');
  const [undoStack, setUndoStack] = useState([]);
  const { logs } = state;

  const suggestions = useMemo(() => suggestAliases(logs), [logs]);
  const rows = useMemo(() => exerciseNameRows(state, today), [state, today]);
  const names = useMemo(() => rows.map(r => r.name), [rows]);
  const q = query.trim().toLowerCase();
  const shown = q ? rows.filter(r => r.name.toLowerCase().includes(q)) : rows;

  const merge = (from, to, dates) => {
    // The undo is taken against the state this sheet rendered, which is
    // what the merge below starts from unless a sync lands in between.
    const patch = undoPatch(state, renameExerciseEverywhere(state, from, to));
    setState(prev => renameExerciseEverywhere(prev, from, to));
    setUndoStack(s => [...s, { from, to, dates, patch }]);
    setOpenKey(null);
    if (onMerged) onMerged(from, to);
  };

  const undo = () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    setState(prev => applyUndo(prev, last.patch));
    setUndoStack(s => s.slice(0, -1));
  };
  const last = undoStack[undoStack.length - 1];

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div role="dialog" aria-label="Exercise names" onClick={e => e.stopPropagation()} style={{
        background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16,
        width: '100%', maxWidth: 560, height: '88vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)',
      }}>
        <div style={{ padding: '10px 10px 10px 18px', borderBottom: `1px solid ${t.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Exercise names</div>
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
              {plural(rows.length, 'logged name')}
              {suggestions.length > 0 && ` · ${suggestions.length} possible duplicate${suggestions.length === 1 ? '' : 's'}`}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            width: 40, height: 40, background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}><X size={20} /></button>
        </div>

        {last && (
          <div role="status" style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px 8px 18px',
            background: t.accentSoft, borderBottom: `1px solid ${t.accentBorder}`,
          }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: t.text, lineHeight: 1.4 }}>
              Merged <b>{last.from}</b> into <b>{last.to}</b> ({plural(last.dates, 'date')}).
            </span>
            <button type="button" onClick={undo} style={{
              minHeight: 40, padding: '0 14px', borderRadius: 10, border: `1px solid ${t.accentBorder}`,
              background: t.cardBg, color: t.accent, fontSize: 13, fontWeight: 700, fontFamily: FONT_BODY,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
            }}><Undo2 size={15} /> Undo</button>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 18px 24px', WebkitOverflowScrolling: 'touch' }}>
          <SectionTitle t={t}>Look like the same lift</SectionTitle>
          {suggestions.length === 0 ? (
            <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.5 }}>
              No likely duplicates. To fold one name into another anyway, tap it below.
            </div>
          ) : suggestions.map(s => {
            const key = `s|${s.from}|${s.to}`;
            return (
              <Suggestion key={key} t={t} c={c} state={state} s={s} open={openKey === key}
                onToggle={() => setOpenKey(k => (k === key ? null : key))} onMerge={merge} />
            );
          })}

          <SectionTitle t={t}>All names</SectionTitle>
          {rows.length > 8 && (
            <div style={{ position: 'relative', marginBottom: 4 }}>
              <Search size={14} color={t.textFaint} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              <Input t={t} placeholder="Filter names…" value={query} onChange={e => setQuery(e.target.value)}
                style={{ paddingLeft: 32, minHeight: 40 }} />
            </div>
          )}
          {rows.length === 0 && (
            <div style={{ fontSize: 12, color: t.textMuted }}>Nothing logged yet.</div>
          )}
          {shown.map(row => {
            const key = `n|${row.name}`;
            return (
              <NameRow key={key} t={t} c={c} state={state} row={row} names={names} open={openKey === key}
                onToggle={() => setOpenKey(k => (k === key ? null : key))} onMerge={merge} />
            );
          })}
          <div style={{ fontSize: 11, color: t.textFaint, lineHeight: 1.5, marginTop: 14 }}>
            A merge moves the sets, plan exercises, swaps, programs and muscle settings to the kept name and remembers
            the old one. Faded names were not trained in the last 8 weeks.
          </div>
        </div>
      </div>
    </div>
  );
}
